import { useQuery } from "@apollo/client";
import {
  Badge,
  BaseTable,
  Button,
  Sentiments,
  Sizes,
  Variants
} from "@sede-x/shell-ds-react-framework";
import {
  AddSquare,
  CrossCircle,
  EditOutlined,
  Search
} from "@sede-x/shell-ds-react-framework/build/esm/components/Icon/components";
import {
  ColumnDef,
  SortingState,
  getPaginationRowModel,
  getSortedRowModel
} from "@tanstack/react-table";
import { MackPrivilege } from "auth";
import { useMackAuth } from "auth/AuthenticationProvider";
import { ROWS_PER_PAGE } from "carbonIQ/CarbonIQConstants";
import { TCIQOption, TFilterSelectedSubmit, TTerminalsFilter } from "carbonIQ/carbonIQtypes";
import { loader } from "graphql.macro";
import { useMemo, useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import GlobalHeader from "shared/components/GlobalHeader";
import LoadingPanel from "shared/components/LoadingPanel";
import NoDataTableImage from "shared/components/basetable/NoDataTableImage";
import ColumnText from "shared/components/basetable/cells/ColumnText";
import { GqlResponse } from "types";
import AddOrUpdateTerminal from "./AddOrUpdateTerminal";
import TerminalsSearch from "./TerminalsSearch";

const carbonTerminalsFilterBy = loader("../graphql/query-CarbonTerminalsFilterBy.graphql");

type TTerminalsFilterByResponse = GqlResponse<TTerminalsFilter[], "ciTerminalsFilterBy">;

const gridColumns: ColumnDef<TTerminalsFilter>[] = [
  {
    header: "Terminal Code",
    accessorKey: "gsapCode",
    enableSorting: true,
    size: 300,
    cell: arg => <ColumnText width={"300px"}>{arg.getValue() as string}</ColumnText>
  },
  {
    header: "Terminal Name",
    accessorKey: "gsapName",
    enableSorting: true,
    size: 300,
    cell: arg => <ColumnText width={"300px"}>{arg.getValue() as string}</ColumnText>
  },
  {
    header: "Display Name",
    accessorKey: "displayName",
    enableSorting: true,
    size: 300,
    cell: arg => <ColumnText width={"300px"}>{arg.getValue() as string}</ColumnText>
  }
];

const gridColumnsGenerate = (
  clickcallback: (arg: {
    row: {
      original: Partial<TTerminalsFilter>;
    };
  }) => void,
  editSelected: number | undefined,
  hasEditPermission: boolean | undefined = false
) => {
  if (!hasEditPermission) {
    return gridColumns;
  } else {
    const additionalGridColumn = {
      header: "",
      accessorKey: "id",
      cell: (arg: { row: { original: Partial<TTerminalsFilter> } }) => (
        <Button
          className={arg.row.original.id === editSelected ? "editedRow" : ""}
          title="Edit"
          size={Sizes.Small}
          onClick={() => clickcallback(arg)}>
          <EditOutlined />
          Edit
        </Button>
      ),
      size: 40,
      enableSorting: false
    };
    return [...gridColumns, additionalGridColumn];
  }
};

const CarbonIQTerminals = () => {
  const { mackUser } = useMackAuth();

  const hasAddTerminalsPrivilege = mackUser?.hasPrivilege(MackPrivilege.AddCITerminal);

  const hasModifyTerminalsPrivilege = mackUser?.hasPrivilege(MackPrivilege.ModifyCITerminal);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedTerminalSearchFilter, setSelectedTerminalSearchFilter] =
    useState<TFilterSelectedSubmit>();
  const [masterDataList, setMasterDataList] = useState<TTerminalsFilter[]>([]);
  const [editSelected, setEditSelected] = useState<number>();

  //Fetching Carbon IQ  Terminals data
  const {
    loading: lCIQDocument,
    error: eCIQDocument,
    data: filterDataList,
    refetch
  } = useQuery<TTerminalsFilterByResponse>(carbonTerminalsFilterBy, {
    onCompleted: data => {
      if (masterDataList.length <= 0) {
        setMasterDataList(data.ciTerminalsFilterBy);
      }
      setPagination({
        pageIndex: 0,
        pageSize: ROWS_PER_PAGE
      });
    }
  });

  const [addOrUpdateOpen, setAddOrUpdateOpen] = useState(false);
  const [updateOpenTerminalDetails, setUpdateOpenTerminalDetails] =
    useState<TTerminalsFilter | null>(null);

  const columns = gridColumnsGenerate(
    arg => {
      setEditSelected(arg.row.original.id as number);
      setUpdateOpenTerminalDetails(arg.row.original as TTerminalsFilter);
      setAddOrUpdateOpen(true as boolean);
    },
    editSelected,
    hasModifyTerminalsPrivilege
  );

  const [sorting, setSorting] = useState<SortingState>([{ desc: false, id: "gsapCode" }]);

  const [pagination, setPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: ROWS_PER_PAGE //default page size
  });

  const tableOptions = {
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex: false
  };

  const handlAddOrUpdateSubmit = () => {
    setAddOrUpdateOpen(false);

    // Force refresh with network-only policy to bypass cache
    refetch({
      fetchPolicy: "network-only"
    }).then(response => {
      if (response?.data?.ciTerminalsFilterBy) {
        // Update the master data list with fresh data
        setMasterDataList(response.data.ciTerminalsFilterBy);
      }
    });
  };

  const handleAddNewTerminalClick = () => {
    setUpdateOpenTerminalDetails(null);
    setAddOrUpdateOpen(true);
  };

  const handleSearchSubmit = (data: TFilterSelectedSubmit) => {
    setSelectedTerminalSearchFilter(data);
    setSearchOpen(false);

    // Reset pagination when filters change
    setPagination({
      pageIndex: 0,
      pageSize: ROWS_PER_PAGE
    });
  };

  const handleSearchReset = () => {
    setSelectedTerminalSearchFilter(null);

    // Reset pagination when filters are cleared
    setPagination({
      pageIndex: 0,
      pageSize: ROWS_PER_PAGE
    });
  };
  const filteredTerminals = useMemo(() => {
    if (!selectedTerminalSearchFilter?.length) {
      // If no filter, use the master data list
      return masterDataList;
    }

    // Otherwise, filter by terminal ID/value
    const selectedValues = selectedTerminalSearchFilter.map(filter => String(filter.value));
    return masterDataList.filter(terminal =>
      selectedValues.includes(String(terminal.gsapName))
    );
  }, [selectedTerminalSearchFilter, masterDataList]);

  const isLoading = [lCIQDocument].some(elm => elm);
  const isError = [eCIQDocument].some(e => e);

  return (
    <>
      {isError && <ApolloErrorViewer error={eCIQDocument} />}

      {isLoading && <LoadingPanel />}

      <GlobalHeader
        pageName="Terminals"
        filterButtonContent={[
          !!selectedTerminalSearchFilter && (
            <span className="customer-Search-current-filter">
              {!!selectedTerminalSearchFilter &&
                Object.keys(selectedTerminalSearchFilter).length > 0 && (
                  <span
                    className="customer-Search-current-filter-txt"
                    title={selectedTerminalSearchFilter
                      ?.map((elm: TCIQOption) => elm.label)
                      .join(", ")}>
                    {selectedTerminalSearchFilter
                      ?.map((elm: TCIQOption) => elm.label)
                      .join(", ")}
                  </span>
                )}

              {
                <button className="customer-Search-reset" onClick={handleSearchReset}>
                  <CrossCircle />
                  Clear Filter
                </button>
              }
            </span>
          )
        ]}
        descriptiontxt="Please use this screen to add new terminals and manage their display names."
        buttonContent={[
          <Badge
            key={1}
            icon={<Search />}
            sentiment={Sentiments.Information}
            variant={Variants.Filled}
            onClick={() => setSearchOpen(!searchOpen)}
          />,
          hasAddTerminalsPrivilege && (
            <Badge
              key={2}
              icon={<AddSquare />}
              sentiment={Sentiments.Information}
              variant={Variants.Filled}
              onClick={() => handleAddNewTerminalClick()}>
              Add New Terminal
            </Badge>
          )
        ]}
      />

      <BaseTable
        columns={columns}
        data={filteredTerminals}
        className="carboniq-data-table"
        tableOptions={tableOptions}
        emptyStateProps={{ image: <NoDataTableImage /> }}
      />

      {addOrUpdateOpen && (
        <AddOrUpdateTerminal
          onClose={() => setAddOrUpdateOpen(false)}
          onSubmit={handlAddOrUpdateSubmit}
          details={updateOpenTerminalDetails}
        />
      )}

      {searchOpen && (
        <TerminalsSearch
          onClose={() => setSearchOpen(false)}
          onSubmit={handleSearchSubmit}
          masterData={masterDataList}
        />
      )}
    </>
  );
};

export default CarbonIQTerminals;
